'use client';

import * as RadixForm from '@radix-ui/react-form';
import { revalidateLogic, useForm, useStore } from '@tanstack/react-form';
import { useIsWebviewRequest } from '@tuic/lib/src/hooks/use-is-webview-request';
import { twMerge } from '@tuic/tailwind-config';
import { Alert } from '@tuic/web-compositions/core/Alert';
import { Button } from '@tuic/web-compositions/core/Button';
import { Divider } from '@tuic/web-compositions/core/Divider';
import { Skeleton } from '@tuic/web-compositions/core/Skeleton';
import { InteractionHeader } from '@tuic/web-compositions/my-trips/InteractionHeader';
import { ManifestPinDialog } from '@tuic/web-compositions/my-trips/ManifestPinDialog';
import { AlertDialog } from '@tuic/web-compositions/shared/AlertDialog';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Fragment, useMemo, useState, useTransition } from 'react';

import { ManifestPreliminaryStatusView } from './ManifestPreliminaryStatusView';
import { ContactSection } from './sections/ContactSection';
import { IdCardSection } from './sections/IdCardSection';
import { OtherInformationSection } from './sections/OtherInformationSection';
import { PersonalInfoSection } from './sections/PersonalInfoSection';
import { isSalutationSelectable, phoneFromApi } from './utils';
import { createManifestSchema } from './validation';

import type { Manifest, ManifestFormValues } from './types';
import type { SimpleFormApi } from '@lib/form/helper';
import type { PictogramName } from '@tuic/web-compositions/core/Pictogram';
import type {
  I18NFromComponent,
  I18NInterface,
} from '@tuic/web-compositions/utils/i18n';
import type { FC, FormEvent, ReactNode } from 'react';

// TanStack's `fieldMeta` is typed as `Record<DeepKeys, FieldMeta>`. Reading
// `.errors` off a single entry that we've already pulled out as `unknown`
// (e.g. via `Object.values`/`Object.entries`) costs a cast — encapsulate it
// here so the cast lives in one place.
const hasFieldErrors = (meta: unknown): boolean => {
  if (!meta || typeof meta !== 'object') return false;
  const errors = (meta as { errors?: unknown[] }).errors;
  return Array.isArray(errors) && errors.length > 0;
};

// Skeleton row layout per section — keep in sync with the real sections
// (PersonalInfoSection: 2 rows × 2 fields; ContactSection: 1+2+2+2+checkbox; …).
type SkeletonRow = number | 'checkbox';

const SECTION_LAYOUTS: SkeletonRow[][] = [
  [2, 2], // PersonalInfo
  [1, 2, 2, 2, 'checkbox'], // Contact
  [2, 2, 2, 2, 2, 'checkbox', 'checkbox'], // IdCard
  [2, 2, 1, 2, 'checkbox', 'checkbox', 1], // OtherInformation
];

const ManifestSectionSkeletonRow: FC<{ row: SkeletonRow }> = ({ row }) => {
  if (row === 'checkbox') {
    return <Skeleton className="h-5 w-3/4" />;
  }
  return (
    <div className="flex flex-col gap-4 md:flex-row md:gap-8">
      {Array.from({ length: row }, (_, i) => (
        <Skeleton key={i} className="h-[58px] w-full rounded-md" />
      ))}
    </div>
  );
};

const ManifestSectionSkeleton: FC<{ rows: SkeletonRow[] }> = ({ rows }) => (
  <section className="space-y-4">
    <div className="flex items-center gap-4">
      <Skeleton className="h-10 w-10 rounded-full" />
      <Skeleton className="h-7 w-1/3 max-w-[280px]" />
    </div>
    <div className="flex flex-col gap-6">
      {rows.map((row, i) => (
        <ManifestSectionSkeletonRow key={i} row={row} />
      ))}
    </div>
  </section>
);

const ManifestFormFallback: FC = () => (
  <div className="space-y-10 text-marine-high-emphasis">
    <div className="space-y-4">
      <Skeleton className="h-9 w-1/2 max-w-[400px]" />
      <div className="space-y-2">
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-5/6" />
      </div>
    </div>

    <div className="space-y-8">
      {SECTION_LAYOUTS.map((rows, i) => (
        <Fragment key={i}>
          <ManifestSectionSkeleton rows={rows} />
          {i < SECTION_LAYOUTS.length - 1 && <Divider align="horizontal" />}
        </Fragment>
      ))}

      <div className="flex items-center justify-between pt-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-11 w-32 rounded-md" />
      </div>
    </div>
  </div>
);

export type ManifestFormProps = {
  fallback?: boolean;

  headline?: ReactNode;
  subline?: ReactNode;
  pinDialogHeadline?: ReactNode;
  pinDialogSubline?: ReactNode;

  manifest?: Manifest;

  /** First trip's startDate (ISO YYYY-MM-DD). Enables `dateOfIssue < embark`. */
  embarkDate?: string;
  /** Last trip's endDate (ISO YYYY-MM-DD). Enables `validUntil ≥ debark + N months`. */
  debarkDate?: string;

  /** True when the UK-ETA confirmation checkbox should be rendered.
   * Optional so fallback (loading) rendering doesn't need to supply it. */
  showEtaCheckbox?: boolean;
  /** True when the USA/ESTA confirmation checkbox should be rendered.
   * Optional so fallback (loading) rendering doesn't need to supply it. */
  showUsaCheckbox?: boolean;

  /** True when the booking has only one traveller — drives the custom
   * header's back-button to skip the (auto-redirecting) overview. */
  singleTravellerBooking?: boolean;

  entryRequirementsHref?: string;

  /** Called when the full form is submitted */
  onSubmit?: (values: ManifestFormValues) => void;

  /** Whether a submission is currently in progress */
  isSubmitting?: boolean;

  /** Free-text error message returned by the backend after a failed submit.
   * Shown verbatim in the form-level alert; field highlighting is handled by
   * client-side Zod, not by this. */
  serverErrorMessage?: string | null;

  i18n?: I18NFromComponent<typeof PersonalInfoSection> &
    I18NFromComponent<typeof ContactSection> &
    I18NFromComponent<typeof IdCardSection> &
    I18NFromComponent<typeof OtherInformationSection> &
    I18NFromComponent<typeof ManifestPinDialog> &
    I18NInterface<
      | 'manifestFieldErrorsAlert'
      | 'formSave'
      | 'formRequiredNote'
      | 'formBack'
      | 'formTryAgain'
      | 'manifestValidationSalutationRequired'
      | 'manifestValidationFirstNameRequired'
      | 'manifestValidationLastNameRequired'
      | 'manifestValidationStreetAddressRequired'
      | 'manifestValidationZipCodeRequired'
      | 'manifestValidationCountryRequired'
      | 'manifestValidationPassportTypeRequired'
      | 'manifestValidationNationalityRequired'
      | 'manifestValidationPassportNumberRequired'
      | 'manifestValidationPlaceOfBirthRequired'
      | 'manifestValidationPlaceOfIssueRequired'
      | 'manifestValidationDateOfBirthRequired'
      | 'manifestValidationDateOfIssueRequired'
      | 'manifestValidationValidUntilRequired'
      | 'manifestValidationProfessionRequired'
      | 'manifestValidationPreferredLanguageRequired'
      | 'manifestValidationEmergencyNameRequired'
      | 'manifestValidationEmergencyPhoneRequired'
      | 'manifestValidationPhoneRequired'
      | 'manifestValidationMobileRequired'
      | 'manifestValidationDateOfBirthPast'
      | 'manifestValidationDateOfBirthBeforeEmbark'
      | 'manifestValidationDateOfBirthAdultMinAge'
      | 'manifestValidationDateOfBirthChildAge'
      | 'manifestValidationDateOfBirthBabyAge'
      | 'manifestValidationDateOfBirthMaxAge'
      | 'manifestValidationValidUntilFuture'
      | 'manifestValidationDateOfIssueFuture'
      | 'manifestValidationDateOfIssueBeforeEmbark'
      | 'manifestValidationDateOfIssueMaxAge'
      | 'manifestValidationZipCodeFormatDE'
      | 'manifestValidationZipCodeFormatATCH'
      | 'manifestValidationPhoneFormat'
      | 'manifestValidationMobileFormat'
      | 'manifestValidationPassportNumberFormat'
      | 'manifestValidationCityFormat'
      | 'manifestValidationApproveMobilePhoneDisclosureMustAccept'
      | 'manifestValidationEntryRequirementsMustAccept'
      | 'manifestValidationHealthTermsMustAccept'
      | 'manifestValidationDataProtectionMustAccept'
      | 'manifestValidationHasETAMustAccept'
      | 'manifestValidationVisaMustAccept'
      | 'manifestValidationPregnant24WeeksMustAccept'
      | 'manifestValidationDateInvalid'
      | 'manifestValidationEmailInvalid'
      | 'manifestValidationValidUntilAfterIssue'
      | 'manifestValidationValidUntilPassportGrace'
      | 'manifestValidationValidUntilIdCardGrace'
      | 'contactAddressHintText'
      | 'contactAddressHintConfirm'
      | 'contactAddressHintCancel'
    >;

  isPinRequired?: boolean;
  onPinEnter?: (pin: string) => boolean;

  /** When set, replaces the form body (sections + submit button) but keeps
   * the form's custom header above it. Mirrors the preliminary-blocker swap
   * so the header stays visible after a successful submit. */
  successView?: ReactNode;

  /** Preliminary view title (rendered when manifest.preliminary === true). */
  preliminaryTitle?: string;
  /** Preliminary view paragraph as a rendered React tree. */
  preliminaryParagraph?: ReactNode;
  /** Decorative pictogram for the preliminary view. */
  preliminaryPictogram?: PictogramName;
};

const buildInitialValues = (manifest?: Manifest): ManifestFormValues => {
  const data = manifest?.manifestData;
  const personal = data?.personalInfo;
  const contact = data?.contact;
  const credentials = data?.credentials;
  const other = data?.otherInformation;
  const dietary = other?.dietary;
  const emergency = other?.emergencyContact;

  return {
    personalInfo: {
      salutation: personal?.salutation ?? '',
      title: personal?.title ?? '',
      firstName: personal?.firstName ?? '',
      lastName: personal?.lastName ?? '',
    },
    contact: {
      streetAddress: contact?.streetAddress ?? '',
      zipCode: contact?.zipCode ?? '',
      city: contact?.city ?? '',
      country: contact?.country ?? '',
      email: contact?.email ?? '',
      phone: phoneFromApi(contact?.phone),
      mobile: phoneFromApi(contact?.mobile),
      approveMobilePhoneDisclosure:
        contact?.approveMobilePhoneDisclosure ?? false,
    },
    credentials: {
      passportType: credentials?.passportType ?? '',
      nationality: credentials?.nationality ?? '',
      passportNumber: credentials?.passportNumber ?? '',
      placeOfBirth: credentials?.placeOfBirth ?? '',
      placeOfIssue: credentials?.placeOfIssue ?? '',
      dateOfBirth: credentials?.dateOfBirth ?? '',
      dateOfIssue: credentials?.dateOfIssue ?? '',
      birthName: credentials?.birthName ?? '',
      validUntil: credentials?.validUntil ?? '',
      profession: credentials?.profession ?? '',
      entryRequirementsAccepted:
        credentials?.entryRequirementsAccepted ?? false,
      hasVisa: credentials?.hasVisa ?? false,
      hasETA: credentials?.hasETA ?? false,
    },
    otherInformation: {
      mealRequest: dietary?.mealRequest ?? '',
      individualRequest: dietary?.individualRequest ?? '',
      allergy: dietary?.allergy ?? [],
      allergyInfo: dietary?.allergyInfo ?? '',
      handicap: other?.handicap ?? '',
      emergencyContactName: emergency?.name ?? '',
      emergencyContactPhone: phoneFromApi(emergency?.phone),
      acceptDataProtectionTerms: other?.acceptDataProtectionTerms ?? false,
      acceptHealthTerms: other?.acceptHealthTerms ?? false,
      pregnant24Weeks: other?.pregnant24Weeks ?? false,
      preferredLanguageCode: data?.preferredLanguageCode ?? '',
    },
  };
};

// A street address without any digit is almost certainly missing a house
// number — we don't hard-reject it (some rural / international addresses
// really have no number), but we ask the user to confirm before submitting.
const addressMissingHouseNumber = (street: string): boolean =>
  !!street && !/\d/.test(street);

const ManifestFormHeader: FC<{
  travellerName?: string;
  backLabel?: string;
}> = ({ travellerName, backLabel }) => {
  const pathname = usePathname() ?? '';
  const isWebview = useIsWebviewRequest();
  const backHref =
    '/' + pathname.split('/').filter(Boolean).slice(0, -1).join('/');

  if (isWebview) {
    return null;
  }

  return (
    <div className="sticky top-0 z-10 mb-8 bg-section-white md:mb-4 xl:mb-8">
      <InteractionHeader
        title={travellerName ?? <Skeleton className="h-2 w-[150px]" />}
        leftButton={{
          iconName: 'arrow-left',
          variant: 'secondary',
          label: backLabel,
          children: <Link href={backHref || '/'} />,
        }}
      />
    </div>
  );
};

export const ManifestForm: FC<ManifestFormProps> = props => {
  const isWebview = useIsWebviewRequest();
  const travellerName =
    `${props.manifest?.manifestData?.personalInfo?.firstName ?? ''} ${props.manifest?.manifestData?.personalInfo?.lastName ?? ''}`.trim();

  // pt-6 replaces the spacing the hidden in-page header used to provide
  // (same pattern as cabin/checklist/contact/internet in webview).
  const webviewSpacing = twMerge(isWebview && 'pt-6');

  if (props.fallback) {
    return (
      <div className={webviewSpacing}>
        <div className="flex justify-center w-full h-[64px] py-2 md:py-4 mb-12">
          <div className="relative flex-1 max-w-[1312px]">
            <div className="absolute left-0 top-1/2 -translate-y-1/2">
              <Skeleton className="rounded-full size-[48px] md:size-[36px]" />
            </div>
            <div className="px-4 h-full flex items-center justify-center text-center overflow-hidden mx-[calc(1_*_48px_+_2_*_0.5rem)] md:mx-[36px]">
              <Skeleton className="h-2 w-[150px]" />
            </div>
          </div>
        </div>
        <ManifestFormFallback />
        {props.isPinRequired && (
          <ManifestPinDialog
            open
            closeable={false}
            headline={props.pinDialogHeadline}
            subline={props.pinDialogSubline}
            onPinEnter={props.onPinEnter}
            travellerName={travellerName}
            i18n={props.i18n}
          />
        )}
      </div>
    );
  }
  return (
    <div className={webviewSpacing}>
      <ManifestFormHeader
        travellerName={travellerName || undefined}
        backLabel={props.i18n?.formBack}
      />
      {props.successView ?? <ManifestFormContent {...props} />}
    </div>
  );
};

const ManifestFormContent: FC<ManifestFormProps> = ({
  headline,
  subline,
  pinDialogHeadline,
  pinDialogSubline,
  onPinEnter,
  onSubmit,
  isSubmitting,
  serverErrorMessage,
  i18n,
  isPinRequired,
  manifest,
  embarkDate,
  debarkDate,
  showEtaCheckbox,
  showUsaCheckbox,
  entryRequirementsHref,
  preliminaryTitle,
  preliminaryParagraph,
  preliminaryPictogram,
}) => {
  const salutation = manifest?.manifestData?.personalInfo?.salutation ?? '';
  const salutationRequired = isSalutationSelectable(manifest);

  const schema = useMemo(
    () =>
      createManifestSchema(i18n, {
        embarkDate,
        debarkDate,
        salutation,
        salutationRequired,
        showEtaCheckbox,
        showUsaCheckbox,
      }),
    [
      i18n,
      embarkDate,
      debarkDate,
      salutation,
      salutationRequired,
      showEtaCheckbox,
      showUsaCheckbox,
    ],
  );

  // Address-hint dialog: when the schema passes but the street has no digits,
  // we park the validated values here and open the dialog.
  const [pendingSubmit, setPendingSubmit] = useState<ManifestFormValues | null>(
    null,
  );

  const form = useForm({
    defaultValues: buildInitialValues(manifest),
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    validators: { onDynamic: schema },
    onSubmit: ({ value }) => {
      if (addressMissingHouseNumber(value.contact.streetAddress)) {
        setPendingSubmit(value);
        return;
      }
      onSubmit?.(value);
    },
  });

  const fullName =
    `${manifest?.manifestData?.personalInfo?.firstName ?? ''} ${manifest?.manifestData?.personalInfo?.lastName ?? ''}`.trim();

  const isPreliminary = !!manifest?.preliminary;
  const isLocked = !!manifest?.locked;
  const isDisabled = isSubmitting || isLocked || isPreliminary;

  const router = useRouter();
  const [isRefetching, startRefetchTransition] = useTransition();

  const handleTryAgain = (): void => {
    startRefetchTransition(() => {
      router.refresh();
    });
  };

  // Open field-level errors after ≥1 submit attempt.
  // Auto-clears once all are fixed. Covers client + server-mapped field errors.
  const hasValidationErrors = useStore(form.store, state => {
    if ((state.submissionAttempts ?? 0) === 0) return false;
    return Object.values(state.fieldMeta ?? {}).some(hasFieldErrors);
  });

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    const formEl = event.currentTarget;
    void form.handleSubmit().then(() => {
      const erroredFields = Object.entries(form.state.fieldMeta ?? {})
        .filter(([, meta]) => hasFieldErrors(meta))
        .map(([name]) => name);
      if (erroredFields.length === 0) return;

      // Scroll to and focus the first field (in DOM order) that has a
      // validation error. Some primitives (Select, PhoneNumberInput, Birthdate)
      // render a hidden `<input name=X>` alongside the visible controls —
      // we skip hidden matches and fall back to the nearest visible ancestor.
      const errored = new Set(erroredFields);
      const isVisible = (el: HTMLElement): boolean =>
        el.offsetParent !== null &&
        !(el instanceof HTMLInputElement && el.type === 'hidden');

      const candidates = Array.from(
        formEl.querySelectorAll<HTMLElement>('[name]'),
      ).filter(el => errored.has(el.getAttribute('name') ?? ''));

      const visible = candidates.find(isVisible);
      if (visible) {
        visible.scrollIntoView({ behavior: 'smooth', block: 'center' });
        visible.focus();
        return;
      }

      // All matches were hidden — walk up from the first match to a visible
      // ancestor and focus the first visible input inside it.
      const hidden = candidates[0];
      if (!hidden) return;
      let cursor: HTMLElement | null = hidden;
      while (cursor && !isVisible(cursor) && cursor !== formEl) {
        cursor = cursor.parentElement;
      }
      if (cursor && cursor !== formEl) {
        cursor.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = cursor.querySelector<HTMLElement>(
          'input:not([type=hidden]), textarea, button',
        );
        focusable?.focus();
      }
    });
  };

  // Cast to the shared type used by section props — bypasses TanStack's
  // 12-generic `ReactFormExtendedApi` at the component boundary.
  const sectionForm = form as unknown as SimpleFormApi<ManifestFormValues>;

  if (isPreliminary) {
    return (
      <ManifestPreliminaryStatusView
        title={preliminaryTitle}
        paragraph={preliminaryParagraph}
        pictogram={preliminaryPictogram}
        action={
          i18n?.formTryAgain ? (
            <Button
              onClick={handleTryAgain}
              indicating={isRefetching}
              disabled={isRefetching}
            >
              {i18n.formTryAgain}
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <div className="space-y-10 text-marine-high-emphasis">
      <div className="space-y-4">
        {headline && <h1 className="headline-lg">{headline}</h1>}
        {subline && <div className="text-lg">{subline}</div>}
      </div>

      {(hasValidationErrors || serverErrorMessage) && (
        <Alert
          mode="error"
          headline={
            hasValidationErrors ? i18n?.manifestFieldErrorsAlert : undefined
          }
        >
          {serverErrorMessage && (
            <span className="whitespace-pre-line">{serverErrorMessage}</span>
          )}
        </Alert>
      )}

      <RadixForm.Root
        onSubmit={handleFormSubmit}
        className="space-y-8"
        noValidate
      >
        <PersonalInfoSection
          form={sectionForm}
          manifest={manifest}
          disabled={isDisabled}
          i18n={i18n}
        />
        <Divider align="horizontal" />
        <ContactSection
          form={sectionForm}
          manifest={manifest}
          disabled={isDisabled}
          i18n={i18n}
        />
        <Divider align="horizontal" />
        <IdCardSection
          form={sectionForm}
          manifest={manifest}
          isDisabled={isDisabled}
          entryRequirementsHref={entryRequirementsHref}
          showEtaCheckbox={showEtaCheckbox ?? false}
          showUsaCheckbox={showUsaCheckbox ?? false}
          i18n={i18n}
        />
        <Divider align="horizontal" />

        <OtherInformationSection
          form={sectionForm}
          manifest={manifest}
          isDisabled={isDisabled}
          i18n={i18n}
        />

        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-marine-medium-emphasis">
            {i18n?.formRequiredNote}
          </p>
          <RadixForm.Submit asChild>
            <Button
              type="submit"
              indicating={isSubmitting}
              disabled={isDisabled}
              tabIndex={isDisabled ? -1 : undefined}
              className="w-full md:w-auto"
            >
              {i18n?.formSave}
            </Button>
          </RadixForm.Submit>
        </div>
      </RadixForm.Root>

      {isPinRequired && (
        <ManifestPinDialog
          open
          closeable={false}
          headline={pinDialogHeadline}
          subline={pinDialogSubline}
          onPinEnter={onPinEnter}
          travellerName={fullName}
          i18n={i18n}
        />
      )}

      <AlertDialog
        open={pendingSubmit !== null}
        onOpenChange={open => {
          if (!open) setPendingSubmit(null);
        }}
        actions={[
          { title: i18n?.contactAddressHintCancel ?? 'Bearbeiten' },
          {
            title: i18n?.contactAddressHintConfirm ?? 'Bestätigen',
            primary: true,
            onClick: () => {
              const toSubmit = pendingSubmit;
              setPendingSubmit(null);
              if (toSubmit) onSubmit?.(toSubmit);
            },
          },
        ]}
      >
        {i18n?.contactAddressHintText}
      </AlertDialog>
    </div>
  );
};
